import { projectInfoModalStrings } from "@utils/StringConstants";

import { UNITS } from "./units.constant";

export const SESSION_TIMEOUT_MS = 1800000;
export const MIN_PROJECT_NAME_LENGTH = 3;
export const STREAM_NAME_MAX_LENGTH = 50;
export const MAX_PROJECT_NAME_LENGTH = 200;
export const PROJECT_NAME_MAX_LENGTH = 20; //Length of project name
export const COMMON_MIN_MAX_LENGTH = { min: 3, max: 50 };
export const TOAST_DEFAULT_DURATION = 3000; //3 seconds
export const TOAST_DEFAULT_DURATION_ERROR = 5000; //5 seconds
export const DATE_FORMAT = "MMM DD, YYYY, hh:mm:ss A z";
export const LICENSE_DATE_FORMAT = "MMM DD, YYYY";
export const COUNTRY_STATE_ID = 0;
export const CHARGE_BALANCE_THRESHOLD = 0.000099;
export const MAX_CURRENCY_EXCHANGE_RATE = 1000000;
export const MAX_NOTIFICATION_COUNT_DISPLAY = 99;
export const DEFAULT_WATER_TYPE = { waterTypeId: 5, waterSource: "Surface Water" };
export const DEFAULT_WATER_SUBTYPE_UF = { waterSubTypeId: 51, waterSubTypeName: "NTU > 75, TSS > 50, TOC > 10" };
export const DEFAULT_WATER_SUBTYPE_RO = { waterSubTypeId: 30, waterSubTypeName: "SDI < 5" };

export const SATURATION_INDEX_THRESHOLD = 0;
export const SATURATION_PERCENTAGE_THRESHOLD = 100;

export const ASCII_LOWERCASE_A = 96;

export const LANDING_PAGE_PREFERENCES = {
  RECENT_PROJECTS: 1,
  EXAMPLE_PROJECTS: 2,
  NO_PROJECTS: 3,
};

export const DEFAULT_RECENT_PROJECT_COUNT = 10;

/** RO_CONCENTRATE_WATER is a internal water type, used for RO element filters and RO design warnings */
export const WATER_TYPE_IDS = {
  RO_PERMEATE_WATER: 1,
  SOFTENED_WATER: 2,
  MUNICIPAL_WATER: 3,
  WELL_WATER: 4,
  SURFACE_WATER: 5,
  SEA_WATER: 6,
  WASTE_WATER: 7,
  BACKWASH_WATER: 8,
  RO_CONCENTRATE_WATER: 9,
};

export const WATER_TYPE_WEIGHT_MAPPING = {
  [WATER_TYPE_IDS.RO_PERMEATE_WATER]: 1,
  [WATER_TYPE_IDS.SOFTENED_WATER]: 2,
  [WATER_TYPE_IDS.MUNICIPAL_WATER]: 3,
  [WATER_TYPE_IDS.WELL_WATER]: 4,
  [WATER_TYPE_IDS.SURFACE_WATER]: 5,
  [WATER_TYPE_IDS.SEA_WATER]: 6,
  [WATER_TYPE_IDS.RO_CONCENTRATE_WATER]: 7,
  [WATER_TYPE_IDS.WASTE_WATER]: 8,
  [WATER_TYPE_IDS.BACKWASH_WATER]: 9,
};

export const USER_INFO = {
  languageId: 1,
  name: "English (USA)",
  enabled: true,
  cultureName: "en",
  translatedName: "English (USA)",
};

export const TIME_UNITS = {
  minutes: "minute",
  hours: "hour",
  days: "day",
  weeks: "week",
  months: "month",
  years: "year",
};

export const EMAIL_VALIDATOR = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const KEYBOARD_KEYS = {
  ENTER: "Enter",
  TAB: "Tab",
  SPACE: " ",
  ARROWUP: "ArrowUp",
  ARROWDOWN: "ArrowDown",
  BACKSPACE: "Backspace",
  DELETE: "Delete",
  ARROWLEFT: "ArrowLeft",
  ARROWRIGHT: "ArrowRight",
  HOME: "Home",
  END: "End",
};

export const TECHNOLOGIES = {
  UF: "UF",
  IX: "IX",
  RO: "RO",
  IXD: "IXD",
  IXN: "IXN",
  IXB: "IXB",
  IXSD: "IXSD",
  IXMB: "IXMB",
  IXOS: "IXOS",
  CCRO: "CCRO",
  IXCP: "IXCP",
  ROSC: "ROSC",
  WQA: "WQA",
  ADJUSTMENT: "ADJUSTMENT",
  MIX: "MIX",
};

export const TECH_TREATMENTS_ID = {
  UF: 1,
  IXSD: 2,
  IXOS: 3,
  RO: 4,
  IXD: 5,
  ROSC: 6,
  IXN: 7,
  IXB: 8,
  IXMB: 9,
  IXCP: 10,
  EDI: 11,
  SPLITNODE: 12,
  WQA: 13,
  // SFACTOR: 13,
  CCRO: 14,
};

export const PROJECT_HOME_TABS_ID = {
  DESIGN: -2,
  FEED: -1,
  ...TECH_TREATMENTS_ID,
  IXSD: TECH_TREATMENTS_ID.IXSD,
  REPORT: 1000,
  RO: 41,
};

export const PROJECT_TAB_IDS = {
  SYSTEM_DESIGN: "systemDesign",
  FEED_SETUP: "feedSetup",
  REPORT: "report",
};

export const TECH_CATEGORY_MAPPING = {
  UF: 1,
  RO: 2,
  IX: 3,
  SPLITNODE: 4,
  SFACTOR: 5,
};

export const IX_TECHNOLOGIES = [
  TECHNOLOGIES.IX,
  TECHNOLOGIES.IXD,
  TECHNOLOGIES.IXMB,
  TECHNOLOGIES.IXN,
  TECHNOLOGIES.IXB,
  TECHNOLOGIES.IXSD,
  TECHNOLOGIES.IXOS,
  TECHNOLOGIES.IXCP,
];

/** Set of all RO-family technology names for fast membership checks. */
export const RO_TECH_SET = new Set([TECHNOLOGIES.RO, TECHNOLOGIES.CCRO]);

/** Set of all IX-family technology names for fast membership checks. */
export const IX_TECH_SET = new Set(IX_TECHNOLOGIES);

export const CHEMICALS_SYMBOL_MAPPING = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "₄": "4",
  "₅": "5",
  "₆": "6",
  "₇": "7",
  "₈": "8",
  "₉": "9",
};

// As per bug 141619 hard limit is set for feed setup temperature as per the technology selected.
/* Ranges are always in Celsius */
export const TEMPERARTURE_RANGES = {
  soft: {
    min: 1,
    max: {
      UF: 40.0,
      IXD: 60.0,
      IXSD: 60.0,
      IXMB: 60.0,
      IXOS: 60.0,
      IXN: 60.0,
      IXB: 60.0,
      IXCP: 60.0,
      RO: 45.0,
      CCRO: 45.0,
    },
  },
  hard: {
    min: 0,
    max: {
      UF: 40.0,
      IXD: 60.0,
      IXSD: 100.0,
      IXMB: 60.0,
      IXOS: 60.0,
      IXN: 60.0,
      IXB: 60.0,
      IXCP: 60.0,
      RO: 80.0,
      CCRO: 80.0,
    },
  },
};
export const FLOW_METRIC_UNIT = "m³/h";

export const DEFAULT_RECOVERY_VALUES = {
  RO: 75,
  UF: 95,
  IXMB: 97,
  IXD: 97,
  IXN: 97,
  IXB: 97,
  IXCP: 97,
  IXOS: 97,
  CCRO: 90,
  [TECHNOLOGIES.IXSD]: 97,
};

export const STATUS_CODE = {
  SUCCESS: 200,
  NO_CONTENT: 204,
};

export const PERCENTAGE_RANGE = {
  min: 0,
  max: 100,
};

export const CustomModalButtons = {
  CANCEL: "modal-cancel-btn",
  CONFIRM: "modal-confirm-btn",
  CLOSE: "modal-close-icon",
  DELETE: "modal-delete-btn",
  CLOSE_BTN: "modal-close-btn",
};

export const DEFAULT_STATE = t => ({ stateName: t("openProjects.defaultState") });
export const DEFAULT_COUNTRY = t => ({ countryName: t("openProjects.defaultCountry") });
export const DEFAULT_MARKET_SEGMENT = t => ({ segmentName: t("openProjects.defaultMarketSegment") });
export const DEFAULT_MARKET_SEGMENT_FILTER = { segmentName: projectInfoModalStrings.defaultMarketSegmentFilter };

export const EXPORT_EXTENSION = ".dwpro";
export const MAX_IMPORT_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const FILTER_OPTIONS_OPEN_PROJECT = t => [
  {
    label: t("openProjects.equal"),
    value: "equal",
  },

  {
    label: t("openProjects.doesnotequal"),
    value: "doesnotequal",
  },
  {
    label: t("openProjects.beginswith"),
    value: "beginswith",
  },
  {
    label: t("openProjects.endswith"),
    value: "endswith",
  },
  {
    label: t("openProjects.contains"),
    value: "contains",
  },
  {
    label: t("openProjects.doesnotcontains"),
    value: "doesnotcontains",
  },
];

export const FILE_ACCESS_ACTIONS = {
  NEW_PROJECT: "openNewProject",
  OPEN_PROJECT: "showOpenProject",
  RECENT_PROJECT: "recentProjectClick",
  NAVIGATE_HOME: "navigateHome",
};

export const HTML_UNICODES = {
  DOWN_ARROW: "\u2193",
  UP_ARROW: "\u2191",
  SPACE: "\u00A0",
  CHECK_MARK: "\u2713",
};

export const LANGUAGE_CODES = {
  1: { code: "en", flag: "/images/language-pref/usa-flag.svg" },
  2: { code: "es", flag: "/images/language-pref/spanish-flag.svg" },
  8: { code: "zh", flag: "/images/language-pref/chinese-flag.svg" },
};

export const TEMPERATURE_FRACTIONS = {
  [UNITS.celsius]: 1,
  [UNITS.farhenite]: 1,
};

export const DATE_FORMATS = {
  MMM_DD_YYYY: "MMM DD, YYYY",
  DD_MM_YYYY: "DD/MM/YYYY",
  MM_DD_YYYY: "MM/DD/YYYY",
  YYYY_MM_DD: "YYYY-MM-DD",
};

export const LICENSE_ROLE_ID = {
  superAdmin: 1,
  licenseAdmin: 2,
  warrantyAdmin: 3,
  databaseAdmin: 4,
  standardUser: 5,
};

/**
 * TODO: Temporary — remove once the gated features (WQA, IXSD, IXB, etc.) are approved and rolled out
 *
 * List of internal DuPont usernames (without @dupont.com) allowed to access
 * features that are still under review on non-dev environments.
 *
 * @type {Set<string>}
 * @todo Remove this list and the `isEnforceTechRestrictions` check in appUtils.js once
 *       the gated features (WQA, IXSD, IXB, etc.) are approved and rolled out
 *       to all users on site.
 */
export const INTERNAL_WAVEPRO_USERS = new Set([
  "pardeep.sharma@dupont.com",
  // "mohamad.akramm@dupont.com",
  "priyanka.sinha-1@dupont.com",
  "ramesh.k.prajapathi@dupont.com",
  "taushik.khan@dupont.com",
  "agawale.g.singh@dupont.com",
  "dupont.chatbot@dupont.com",
  "indra.chatterjee@dupont.com",
  "taslim.ahmed@dupont.com",
  "renduchintala.v.vijayanand@dupont.com",
  "parimal.joshi@dupont.com",
  "david.romero@dupont.com",
  "anish.nair@dupont.com",
  "manisha.parhar@dupont.com",

  "garth.parkerjr@dupont.com",
  "claudia.niewersch@dupont.com",
  "supriyo.das@dupont.com",
]);
